import React, { useState } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { Button, Modal, Tabs, TextInput, Textarea } from "@opendate-app/setlist";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import { currencySymbol, toCents } from "../giftCardLabels";

const ADD = "add";
const REMOVE = "remove";

const GiftCardAdjustModal = ({ giftCard, canAdd, canRemove, maxAddCents, removableCents, submitting, onClose, onSubmit }) => {
  const [direction, setDirection] = useState(canAdd ? ADD : REMOVE);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [idempotencyToken] = useState(uuidv4);

  const money = (cents) => formatCents(cents, giftCard.currency);
  const amountCents = toCents(amount);
  const removing = direction === REMOVE;
  const limitCents = removing ? removableCents : maxAddCents;
  const overLimit = amountCents > limitCents;
  const newBalanceCents = giftCard.balanceCents + (removing ? -amountCents : amountCents);
  const ready = amountCents > 0 && !overLimit && note.trim().length > 0 && !submitting;
  const limitMessage = removing
    ? `You can remove up to ${money(removableCents)} from this card`
    : `You can add up to ${money(maxAddCents)} at a time`;

  const directions = [canAdd && { value: ADD, label: "Add" }, canRemove && { value: REMOVE, label: "Remove" }].filter(Boolean);

  const footer = (
    <div className="gift-card-modal-footer">
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={!ready}
        onClick={() => onSubmit({ direction, amountCents, note, idempotencyToken })}
      >
        {`${removing ? "Remove" : "Add"} ${money(amountCents)}`}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      icon={false}
      title="Adjust balance"
      description={`•••• ${giftCard.last4} · balance ${money(giftCard.balanceCents)}`}
      footer={footer}
    >
      <div className="gift-card-modal-body">
        {directions.length > 1 && (
          <Tabs variant="button-border" fullWidth items={directions} value={direction} onChange={setDirection} />
        )}
        <TextInput
          label={removing ? "Amount to remove" : "Amount to add"}
          inputMode="decimal"
          leadingText={currencySymbol(giftCard.currency)}
          placeholder="0.00"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          hint={limitMessage}
          error={overLimit ? limitMessage : null}
        />
        <Textarea
          label="Note (required)"
          rows={3}
          placeholder="Why are you changing the balance?"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="gift-card-modal-preview" data-testid="adjust-preview">
          <span>New balance</span>
          <strong>{overLimit ? "—" : money(newBalanceCents)}</strong>
        </div>
      </div>
    </Modal>
  );
};

GiftCardAdjustModal.propTypes = {
  giftCard: PropTypes.shape({
    last4: PropTypes.string.isRequired,
    currency: PropTypes.string,
    balanceCents: PropTypes.number.isRequired,
  }).isRequired,
  canAdd: PropTypes.bool.isRequired,
  canRemove: PropTypes.bool.isRequired,
  maxAddCents: PropTypes.number.isRequired,
  removableCents: PropTypes.number.isRequired,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

GiftCardAdjustModal.defaultProps = {
  submitting: false,
};

export default GiftCardAdjustModal;
